import { SheetBody, SheetDescription, SheetHeader, SheetTitle } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { OpportunityFindings } from "@/features/opportunities/components/drawer/OpportunityFindings/OpportunityFindings";
import { OpportunityMetrics } from "@/features/opportunities/components/drawer/OpportunityMetrics/OpportunityMetrics";
import { OpportunityActionBadge } from "@/features/opportunities/components/table/OpportunityActionBadge/OpportunityActionBadge";
import { OpportunityStatusBadge } from "@/features/opportunities/components/table/OpportunityStatusBadge/OpportunityStatusBadge";
import { formatOpportunityEngineLabel } from "@/features/opportunities/lib/format-opportunity";

import type { HubChromeOpportunityRow } from "./hub-chrome.types.js";
import { asOpportunityAction, drivingIdentifier } from "./hub-opportunity-columns.js";

const MAIN_SUB_FEATURE = "Main";

/** Competitor opportunity body, laid out like the legacy opportunities drawer minus creative examples. */
export function OpportunityHubCompetitorDetails({ row }: { row: HubChromeOpportunityRow }) {
  const { t } = useTranslation();
  const featureLabel =
    row.feature && row.subFeature && row.subFeature !== MAIN_SUB_FEATURE
      ? `${row.feature} / ${row.subFeature}`
      : row.feature;
  const metaParts = [
    { label: row.dataset },
    { label: row.engine ? formatOpportunityEngineLabel(row.engine, t) : undefined },
    { label: featureLabel },
    { label: row.industry?.replace(/_/g, " "), className: "capitalize" },
  ].filter((part): part is { label: string; className?: string } => Boolean(part.label));
  const hasAnalysis = Boolean(row.findings || row.suggestion);

  return (
    <>
      <SheetHeader className="border-b px-6 pb-5 pt-6">
        <div className="flex flex-col gap-1.5 pl-10">
          <SheetTitle className="break-words hyphens-auto text-base leading-snug">
            {drivingIdentifier(row)}
          </SheetTitle>
          <SheetDescription className="text-sm leading-snug text-foreground/70">
            {row.topicDescription ?? row.setName}
          </SheetDescription>
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-muted-foreground/60">
            {metaParts.map((part, index) => (
              <span key={index} className={part.className}>
                {index > 0 ? <span aria-hidden="true" className="mr-1.5">·</span> : null}
                {part.label}
              </span>
            ))}
            {metaParts.length > 0 ? (
              <span aria-hidden="true" className="mx-1 opacity-30">|</span>
            ) : null}
            <OpportunityStatusBadge status={row.legacyState === "rejected" ? "rejected" : "active"} />
            <OpportunityActionBadge action={asOpportunityAction(row.action)} />
          </div>
        </div>
      </SheetHeader>
      <SheetBody className="flex flex-col p-0">
        <div className="px-6 py-5">
          <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("opportunities.drawer.spendComparison")}
          </p>
          <OpportunityMetrics
            yourSpend={row.spendUsd ?? null}
            yourSpendPerDataset={row.spendPerAngle ?? null}
            competitorSpend={row.competitorSpendUsd ?? null}
            competitorSpendPerDataset={row.competitorSpendPerAngle ?? null}
            benchmarkSpend={row.benchmarkSpendUsd ?? null}
            benchmarkSpendPerDataset={row.benchmarkSpendPerAngle ?? null}
            feature={row.feature}
            subFeature={row.subFeature}
          />
        </div>
        {hasAnalysis ? (
          <div className="px-6 py-5">
            <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("opportunities.drawer.analysis")}
            </p>
            <OpportunityFindings finding={row.findings ?? ""} suggestion={row.suggestion ?? ""} />
          </div>
        ) : null}
      </SheetBody>
    </>
  );
}
